import { ROLE } from './../model/user.model';
import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, switchMap, of, catchError, throwError } from 'rxjs';
import { AccountRequest } from 'src/app/component/auth/model/account-request/account-request.model';
import { BaseRestApiService } from 'src/app/service/baseRestApiService.service';
import { UrlProviderService, EndPoints } from 'src/app/service/urlprovider.service';
import { Region } from '../../../model/region.model';
import { PartyRoleAccountRequest } from '../model/account-request/party-role-account-request.model';
import { Organization } from '../../common-components/organizations/model/organization.model';
import { AccountRequestSignature } from '../model/account-request/account-request-signature';

@Injectable({
    providedIn: 'root'
})
export class AccountRequestService extends BaseRestApiService {
    constructor(http: HttpClient, urlProvider: UrlProviderService) {
        super(http, urlProvider);
    }

    show(id: number): Observable<any> {
        return this.get<any>(EndPoints.accountRequest_show, '', id);
    }

    showByEmailConfirmationCode(emailConfirmationCode: string): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_showByEmailConfirmationCode, false, '', {
            emailConfirmationCode: emailConfirmationCode
        });
    }

    create(newAccountRequest: AccountRequest, organization?: Organization, region?: Region): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_create, false, '', {
            newAccountRequest: newAccountRequest,
            organization: organization,
            region: region
        });
    }

    updateAccountRequestByEmailConfirmationCode(
        editAccountRequest: AccountRequest,
        organization: Organization,
        region: Region,
        emailConfirmationCode: string
    ): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_updateAccountRequestByEmailConfirmationCode, false, '', {
            editAccountRequest: editAccountRequest,
            organization: organization,
            region: region,
            emailConfirmationCode: emailConfirmationCode
        });
    }

    update(editAccountRequest: AccountRequest, organization: Organization, region: Region): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_update, false, '', {
            editAccountRequest: editAccountRequest,
            organization: organization,
            region: region
        });
    }

    createAccountRequestRole(accountRequest: AccountRequest, role: ROLE): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_createAccountRequestRole, false, '', {
            role: role,
            accountRequest: accountRequest
        });
    }

    updateAccountRequestRole(accountRequest: AccountRequest, oldRole: ROLE, newRole: ROLE): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_updateAccountRequestRole, false, '', {
            oldRole: oldRole,
            newRole: newRole,
            accountRequest: accountRequest
        });
    }

    deleteAccountRequestRole(accountRequest: AccountRequest, role: ROLE): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_deleteAccountRequestRole, false, '', {
            role: role,
            accountRequest: accountRequest
        });
    }

    releaseControlAccountRequest(accountRequest: AccountRequest): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_releaseControlAccountRequest, false, '', {
            accountRequest: accountRequest
        });
    }

    signAccountRequest(partyRoleAccountRequest: PartyRoleAccountRequest, signature: string): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_signAccountRequest, false, '', {
            partyRoleAccountRequest: partyRoleAccountRequest,
            signature: signature
        });
    }

    deleteSignAccountRequest(accountRequestSignature: AccountRequestSignature): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_deleteSignAccountRequest, false, '', {
            accountRequestSignature: accountRequestSignature
        });
    }

    deleteAllSignAccountRequest(accountRequest: AccountRequest): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_deleteAllSignAccountRequest, false, '', {
            accountRequest: accountRequest
        });
    }

    denyAccountRequest(accountRequest: AccountRequest, nodeId: number, message: string): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_deleteAllSignAccountRequest, false, '', {
            accountRequest: accountRequest,
            nodeId: nodeId,
            message: message
        });
    }

    confirmEmailAddress(emailConfirmationCode: string): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_confirmEmailAddress, false, '', {
            emailConfirmationCode: emailConfirmationCode
        });
    }

    declineEmailAddress(emailConfirmationCode: string): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_declineEmailAddress, false, '', {
            emailConfirmationCode: emailConfirmationCode
        });
    }

    resendEmailConfirmationCode(emailTo: string): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_resendEmailConfirmationCode, false, '', {
            emailTo: emailTo
        });
    }

    sendEmailBackToRequester(accountRequest: AccountRequest, message: string): Observable<any> {
        return this.post<any>(EndPoints.accountRequest_sendEmailBackToRequester, false, '', {
            accountRequest: accountRequest,
            message: message
        });
    }

    showDetails(accountId: number): Observable<any> {
        return this.get<any>(EndPoints.accountRequest_show, null, accountId).pipe(
            switchMap((result: any) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }
}
